import React from 'react';
import { Button, Tag } from 'antd';
import { LinkOutlined } from '@ant-design/icons';

const dash = <span style={{ color: '#bfbfbf' }}>—</span>;

const connectionColumn = (t, isConnected) => ({
  title: t('Connection'),
  key: 'connection',
  width: 150,
  render: (_, record) => (isConnected(record) ? (
    <Tag icon={<LinkOutlined />} color="processing" style={{ fontSize: 11, margin: 0 }}>
      {t('Connected supplier')}
    </Tag>
  ) : dash),
});

const linkCell = (value, onOpen, record) => (value ? (
  <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => onOpen?.(record)}>
    {value}
  </Button>
) : dash);

/**
 * Products step columns (inquiry-module-redesign follow-up) — Supplier Item
 * Number, Product, Type, Supplier, Average Item Price. Type renders exactly
 * like the Products Module's Products tab (same Tag colours/labels) since
 * it's the same `productType` field, just resolved via the row's
 * `productId` (a Selection Wizard row is a Supplier_Product record, which
 * doesn't carry productType itself — see ProductsStep's productTypeById
 * lookup). Product and Supplier are clickable — they open the Product
 * Details / Supplier Profile page and return here afterwards. No Actions
 * column (rows are chosen via row selection, not edited).
 */
export const buildProductsStepColumns = ({ t, onOpenProduct, onOpenSupplier }) => [
  { title: t('Supplier Item Number'), dataIndex: 'supplierItemNumber', key: 'supplierItemNumber', width: 160, render: (v) => v || dash },
  {
    title: t('Product'),
    dataIndex: 'productName',
    key: 'productName',
    render: (v, record) => linkCell(v, onOpenProduct, record),
  },
  {
    title: t('Type'),
    dataIndex: 'productType',
    key: 'productType',
    width: 130,
    render: (productType) => (
      <Tag color={productType === 'Custom' ? 'purple' : 'blue'}>
        {t(productType === 'Custom' ? 'Custom Product' : 'Standard Product')}
      </Tag>
    ),
  },
  {
    title: t('Supplier'),
    dataIndex: 'supplierName',
    key: 'supplierName',
    render: (v, record) => linkCell(v, onOpenSupplier, record),
  },
  {
    title: t('Average Item Price'),
    dataIndex: 'avgItemPrice',
    key: 'avgItemPrice',
    width: 160,
    align: 'right',
    render: (value) => (value != null ? `${Number(value).toLocaleString('da-DK')} DKK` : dash),
  },
];

/**
 * Services step columns — the same columns the Products Module's Services
 * tab shows (Code, Service Name, Supplier, Service Type, Pricing Unit),
 * minus Status and Actions, plus the connected-supplier badge column
 * (Requirement 6.4). Supplier is clickable — opens the Supplier Profile
 * page and returns here afterwards.
 */
export const buildServicesStepColumns = ({ t, isConnected, onOpenSupplier }) => [
  { title: t('Code'), dataIndex: 'code', key: 'code', width: 110, render: (v) => v || dash },
  { title: t('Service Name'), dataIndex: 'productName', key: 'productName', render: (v) => (v ? t(v) : dash) },
  {
    title: t('Supplier'),
    dataIndex: 'supplierName',
    key: 'supplierName',
    render: (v, record) => linkCell(v, onOpenSupplier, record),
  },
  {
    title: t('Service Type'),
    dataIndex: 'serviceType',
    key: 'serviceType',
    width: 130,
    render: (serviceType) => (serviceType ? (
      <Tag color={serviceType === 'Charge' ? 'gold' : 'geekblue'}>{t(serviceType)}</Tag>
    ) : dash),
  },
  { title: t('Pricing Unit'), dataIndex: 'pricingUnit', key: 'pricingUnit', width: 120, render: (v) => (v ? t(v) : dash) },
  connectionColumn(t, isConnected),
];
